import { useCallback, useEffect, useRef, useState } from "react";

import { useHistory } from "react-router-dom";

interface UseNavPromptOptions {
  /** Whether navigation should be blocked. */
  shouldBlock?: boolean;
}

interface UseNavPromptReturn {
  /** Whether a navigation attempt is currently blocked. */
  isBlocked: boolean;
  /** Unblock and continue the pending navigation. */
  continueNavigation: () => void;
  /** Dismiss the prompt without navigating. */
  hidePrompt: () => void;
}

const useNavPrompt = ({
  shouldBlock = true,
}: UseNavPromptOptions): UseNavPromptReturn => {
  const [isBlocked, setIsBlocked] = useState(false);

  const history = useHistory();

  const unblockRef = useRef<(() => void) | undefined>(undefined);
  const navRef = useRef<{ transition: string; action: string } | null>(null);

  const handleNavigation = useCallback(
    (action: string, transition: string) => {
      switch (action) {
        case "PUSH":
          history.push(transition);
          break;
        case "REPLACE":
          history.replace(transition);
          break;
        case "POP":
          history.goBack();
          break;
      }
    },
    [history]
  );

  const continueNavigation = useCallback(() => {
    if (isBlocked && unblockRef.current) {
      unblockRef.current();
      setIsBlocked(false);
      if (navRef.current) {
        const { action, transition } = navRef.current;
        handleNavigation(action, transition);
      }
    }
  }, [isBlocked, handleNavigation]);

  const hidePrompt = useCallback(() => setIsBlocked(false), []);

  useEffect(() => {
    if (shouldBlock) {
      unblockRef.current = history.block(
        (transition: { pathname: string }, action: string) => {
          setIsBlocked(true);
          navRef.current = { transition: transition.pathname, action };

          return false;
        }
      ) as unknown as () => void;

      return () => {
        unblockRef.current?.();
      };
    }

    return undefined;
  }, [shouldBlock, history]);

  return { isBlocked, continueNavigation, hidePrompt };
};

export { useNavPrompt };
export type { UseNavPromptOptions, UseNavPromptReturn };
